import styled from '@emotion/styled';
import { mq } from '../../../styles/mixins';
import { css } from '@emotion/react';
import { Header3, Headline4 } from '../../core/Typography/Typography';
import { THEME } from 'common/consts/theme';

type StageProps = {
  imageUrl: string;
  position: 'TOP' | 'BOTTOM';
};

export const StageWrapper = styled.div<StageProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  gap: 1rem;
  max-width: 13.125rem;

  ${({ position, imageUrl }) =>
    position === 'TOP' &&
    css`
      justify-content: center;
      flex-direction: column-reverse;
      &:after {
        content: '';
        position: absolute;
        background-image: url(${imageUrl});
        background-repeat: no-repeat;
        background-position: center;
        background-size: 5rem 5rem;
        display: block;
        width: 12.313rem;
        height: 12.313rem;
      }

      ${mq.xSmall(css`
        &:after {
          bottom: 25%;
          right: -11rem;
        }

        &:nth-child(1) {
          grid-row: 2;
          grid-column: 1;
        }

        &:nth-child(2) {
          grid-row: 4;
          grid-column: 1;
        }

        &:nth-child(3) {
          grid-row: 6;
          grid-column: 1;
        }
      `)}

      ${mq.medium(css`
        &:after {
          bottom: -10rem;
          right: unset;
        }

        &:nth-child(1) {
          grid-row: 1;
          grid-column: 2;
        }

        &:nth-child(2) {
          grid-row: 1;
          grid-column: 4;
        }

        &:nth-child(3) {
          grid-row: 1;
          grid-column: 6/7;
        }
      `)}
    `}
  ${({ position, imageUrl }) =>
    position === 'BOTTOM' &&
    css`
      flex-direction: column-reverse;
      justify-content: center;

      &:before {
        content: '';
        position: absolute;
        background-image: url(${imageUrl});
        background-repeat: no-repeat;
        background-position: center;
        background-size: 5rem 5rem;
        display: block;
        width: 12.313rem;
        height: 12.313rem;
      }

      ${mq.xSmall(css`
        &:before {
          top: 25%;
          left: -11rem;
        }
        &:nth-child(1) {
          grid-row: 1;
          grid-column: 1;
        }

        &:nth-child(2) {
          grid-row: 3;
          grid-column: 1;
        }

        &:nth-child(3) {
          grid-row: 5;
          grid-column: 1;
        }
      `)}

      ${mq.medium(css`
        flex-direction: column-reverse;
        justify-content: flex-end;

        &:before {
          top: -10rem;
          left: unset;
        }
        &:nth-child(1) {
          grid-row: 1;
          grid-column: 1;
        }

        &:nth-child(2) {
          grid-row: 1;
          grid-column: 3;
        }

        &:nth-child(3) {
          grid-row: 1;
          grid-column: 5;
        }
      `)}
    `}
  
  ${({ position }) =>
    position === 'TOP' &&
    css`
      margin-bottom: -2rem;
    `}
  
  ${({ position }) =>
    position === 'BOTTOM' &&
    css`
      margin-top: -2rem;
    `}
`;

export const ProcessStageText = styled.p`
  color: ${({ theme }) => theme.colors.light_grey};
  font: 300 normal 0.875rem/1.5rem ${({ theme }) => theme.font.nunito},
    sans-serif;
  letter-spacing: 0.0125rem;
  margin: 0;
  text-align: center;
`;

export const ProcessStageTitle = styled.p`
  ${Headline4};
  color: ${({ theme }) => theme.colors.core_white};
  text-align: center;
  margin: 0;
`;

export const ProcessTitle = styled.h1`
  ${Header3};
  color: ${({ theme }) => theme.colors.core_white};
  margin: 0 0 1.25rem 0;
  text-align: center;
  letter-spacing: unset;

  ${mq.large(css`
    color: ${THEME.colors.light_grey};
    letter-spacing: -0.0125rem;
    margin-bottom: 1rem;
  `)};

  ${mq.xLarge(css`
    font-size: 3rem;
    margin-bottom: 1.5rem;
  `)};
`;

export const ProcessSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.light_grey};
  font-family: ${({ theme }) => theme.font.nunito};
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 300;
  max-width: 18.8125rem;
  letter-spacing: 0.0125rem;
  margin: 0 auto 3.5rem auto;
  text-align: center;

  ${mq.medium(css`
    max-width: 33.4375rem;
  `)};

  ${mq.large(css`
    font-size: 1.25rem;
    line-height: 2rem;
    margin-bottom: 5rem;
    max-width: 46.4375rem;
  `)};

  ${mq.xLarge(css`
    margin-bottom: 7.5rem;
  `)};
`;

export const ProcessProgressContainer = styled.div`
  display: grid;
  grid-template-columns: 327px;
  grid-template-rows: 1fr;
  justify-content: center;

  ${mq.medium(css`
    grid-template-columns: 1fr;
  `)};
`;

export const ProcessProgress = styled.div`
  ${mq.large(css`
    max-width: 60rem;
    margin: 0 auto;
  `)};

  ${mq.xLarge(css`
    max-width: 70.89rem;
  `)};
`;
